import { Stack, Text, TooltipHost } from '@fluentui/react';

interface WorkStatusBarProps {
  /** Map of work status code -> count (as returned by the API, stringy). */
  works: Record<string, string | number>;
  /** Status metadata: code -> { name, color }. */
  statusByCode: Map<string, { name: string; color?: string }>;
  /** This contractor's total works (precomputed by the parent for consistency). */
  total: number;
}

// Order segments by descending count so the bar reads consistently and the
// biggest status leads.
const FALLBACK_COLOR = '#8a8886';

const WorkStatusBar = ({ works, statusByCode, total }: WorkStatusBarProps) => {
  const segments = Object.keys(works)
    .map((code) => ({
      code,
      count:
        typeof works[code] === 'number'
          ? (works[code] as number)
          : Number.parseInt(String(works[code]), 10) || 0,
      name: statusByCode.get(code)?.name ?? code,
      color: statusByCode.get(code)?.color ?? FALLBACK_COLOR,
    }))
    .filter((s) => s.count > 0)
    .sort((a, b) => b.count - a.count);

  if (total === 0) {
    return <span>No works</span>;
  }

  const tooltip = (
    <div>
      {segments.map((s) => (
        <div key={s.code}>{`${s.name}: ${s.count}`}</div>
      ))}
    </div>
  );

  // Every contractor gets a full-width bar; statuses split it by percentage.
  return (
    <TooltipHost tooltipProps={{ onRenderContent: () => tooltip }}>
      <Stack
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: 8 }}
        styles={{ root: { width: '100%' } }}
      >
        <Text
          variant="medium"
          styles={{ root: { fontWeight: 600, minWidth: 20 } }}
        >
          {total}
        </Text>
        <div
          style={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            height: 12,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {segments.map((s) => (
            <div
              key={s.code}
              style={{
                width: `${(s.count / total) * 100}%`,
                background: s.color,
              }}
            />
          ))}
        </div>
      </Stack>
    </TooltipHost>
  );
};

export default WorkStatusBar;
